import * as React from "react";
import { styled, css, keyframes } from "styled-components";
import { transformImageUrl } from "@ludens-reklame/rubics-v3-sdk/file/utils";
import { Image } from "@ludens-reklame/rubics-v3-sdk/file/types";
import { mobileBreakPointCSS } from "../../defaults/theme.js";
import { cssSpacing } from "../../util/styles.js";
import { useInViewport } from "../../hooks/useInViewport.js";
import {
  ButtonAction,
  ButtonActionProps,
} from "../../internal-components/Button.js";
import { sun } from "../../icons/index.js";
import {
  Constrainer,
  HorizontalSpacer,
  VerticalSpacer,
} from "../../internal-components/Layout.js";
import { RichText } from "../../internal-components/RichText.js";
import { getColorVar } from "../../util/getColorVar.js";
import { createEditableProps } from "@ludens-reklame/rubics-v3-react/util/fns";

interface BannerImage {
  image: Image;
}

export interface BannerProps
  extends Partial<Pick<BannerAnimationStyledProps, "position">> {
  text?: string;
  images?: BannerImage[];
  actions?: ButtonActionProps[];
  animationColor?: BannerAnimationStyledProps["fillColor"];
}

export const BannerAnimation: React.FC<BannerProps> = ({
  images = [],
  actions = [],
  text = "",
  position = "right",
  animationColor = "primary",
}) => {
  const hasImage = images.length > 0;

  return (
    <VerticalSpacer>
      <HorizontalSpacer>
        <Constrainer>
          <BannerAnimationWrapper position={position} hasImage={hasImage}>
            <BannerAnimationImageContainer hasImage={hasImage}>
              <BannerAnimationGraphic
                hasImage={hasImage}
                position={position}
                fillColor={animationColor}
              >
                {sun}
              </BannerAnimationGraphic>
              <Images images={images} position={position} />
            </BannerAnimationImageContainer>
            <BannerAnimationCard hasImage={hasImage} position={position}>
              <RichText
                dangerouslySetInnerHTML={{ __html: text }}
                {...createEditableProps("html", "text")}
              />
              {actions.length > 0 && (
                <BannerAnimationActions>
                  {(actions.filter((x) => x.link) as ButtonActionProps[]).map(
                    (action, i) => (
                      <ButtonAction key={i} {...action} />
                    )
                  )}
                </BannerAnimationActions>
              )}
            </BannerAnimationCard>
          </BannerAnimationWrapper>
        </Constrainer>
      </HorizontalSpacer>
    </VerticalSpacer>
  );
};

interface Props extends Pick<BannerAnimationStyledProps, "position"> {
  images: BannerImage[];
}

function Images({ images, position }: Props) {
  const ref = useInViewport();
  const imagesProps = React.useMemo(() => {
    return images.map((image) => ({
      src: transformImageUrl(image.image, {
        width: 540,
        height: 540,
        fit: "cover",
      }),
      alt: image.image.altText || "",
    }));
  }, [images]);

  if (imagesProps.length === 0) return null;
  if (imagesProps.length === 1) {
    return <BannerAnimationImgOne {...imagesProps[0]} position={position} />;
  }
  if (imagesProps.length === 2) {
    return (
      <BannerImageGridTwo position={position}>
        <img {...imagesProps[0]} />
        <img {...imagesProps[1]} />
      </BannerImageGridTwo>
    );
  }
  if (imagesProps.length === 3) {
    return (
      <BannerImageGridThree ref={ref} position={position}>
        <img {...imagesProps[0]} />
        <img {...imagesProps[1]} />
        <img {...imagesProps[2]} />
      </BannerImageGridThree>
    );
  }
  return null;
}

export interface BannerAnimationStyledProps {
  position: "right" | "left";
  hasImage?: boolean;
  fillColor?: string;
}

const getColumnsFraction = (props: BannerAnimationStyledProps) => {
  if (!props.hasImage) {
    return "1fr";
  } else if (props.position === "right") {
    return "2fr 3fr";
  }
  return "3fr 2fr";
};

export const BannerAnimationWrapper = styled.div<
  Pick<BannerAnimationStyledProps, "hasImage" | "position">
>`
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
  margin-top: ${(props) => props.theme.spacing.xl};
  overflow-x: hidden;

  @media (min-width: ${mobileBreakPointCSS}) {
    grid-template-columns: ${getColumnsFraction};
    gap: ${(props) => props.theme.spacing.xl};

    ${(props) =>
      props.hasImage &&
      css`
        margin-top: 0;
        align-items: center;
      `};

    ${(props) =>
      props.position === "right" &&
      css`
        > :first-child {
          order: 2;
        }

        > :nth-child(2) {
          order: 1;
        }
      `};
  }
`;

export const BannerAnimationImageContainer = styled.div<
  Pick<BannerAnimationStyledProps, "hasImage">
>`
  position: absolute;
  width: 100%;
  ${(props) =>
    props.hasImage &&
    css`
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;

      @media (min-width: ${mobileBreakPointCSS}) {
        aspect-ratio: 1/1;
      }
    `};
`;

const rotate = keyframes`
  0% {
    transform: rotate(0deg) scale(.85);
  }
  50% {
    transform: rotate(180deg) scale(1);
  }
  100% {
    transform: rotate(360deg) scale(.85);
  }
`;
export const BannerAnimationGraphic = styled.div<BannerAnimationStyledProps>`
  position: absolute;
  overflow: hidden;

  ${(props) =>
    props.hasImage &&
    css`
      top: -${(props) => props.theme.spacing.xl}px;
      width: 400px;
      max-width: 100%;
      height: auto;
      left: ${props.position === "left"
        ? `-${props.theme.spacing.l}`
        : "initial"};
      right: ${props.position === "right"
        ? `-${props.theme.spacing.l}`
        : "initial"};

      @media (min-width: ${mobileBreakPointCSS}) {
        top: 0;
        left: 0;
        right: 0;
        width: 100%;
        height: 100%;
      }
    `};

  ${(props) =>
    !props.hasImage &&
    css`
      width: 400px;
      max-width: 100%;
      left: ${props.position === "right" ? 0 : "initial"};
      right: ${props.position === "left"
        ? `-${props.theme.spacing.l}`
        : "initial"};
      top: -${(props) => props.theme.spacing.xl};

      @media (min-width: ${mobileBreakPointCSS}) {
        right: ${props.position === "left" ? 0 : "initial"};
      }
    `};

  > svg {
    width: 100%;
    height: 100%;
    fill: ${(props) => getColorVar(props.fillColor || "primary")};
    animation: ${rotate} 45s linear infinite;
    > path {
      fill: ${(props) => getColorVar(props.fillColor || "primary")};
    }
  }
`;

export const BannerAnimationCard = styled.div<
  Pick<BannerAnimationStyledProps, "hasImage" | "position">
>`
  z-index: 1;
  padding: ${(props) => props.theme.spacing.l} 0;
  box-sizing: border-box;

  ${(props) =>
    !props.hasImage &&
    css`
      max-width: 600px;
      justify-self: center;
      background-color: var(--swatch4);
      padding: ${(props) => props.theme.spacing.l};
      border-radius: ${props.theme.borderRadius};

      @media (min-width: ${mobileBreakPointCSS}) {
        margin-top: ${(props) => props.theme.spacing.l};
        padding: ${(props) => props.theme.spacing.xl};
        border-radius: ${props.theme.borderRadius};
      }
    `};

  @media (min-width: ${mobileBreakPointCSS}) {
    padding: 0;

    ${(props) =>
      !props.hasImage &&
      css`
        padding: ${cssSpacing("xl")};
      `};
  }
`;

export const BannerAnimationActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${(props) => props.theme.spacing.m};
  margin-top: ${(props) => props.theme.spacing.l};
  flex-direction: column;

  a {
    align-self: flex-start;
    margin: auto;
  }

  @media (min-width: ${mobileBreakPointCSS}) {
    a {
      margin: 0;
    }
  }
`;

export const BannerAnimationImg = styled.img`
  position: relative;
  display: block;
  object-fit: contain;
  width: 100%;
  height: auto;

  @media (min-width: ${mobileBreakPointCSS}) {
    width: 60% !important;
  }
`;

export const BannerAnimationImgOne = styled(BannerAnimationImg)<
  Pick<BannerAnimationStyledProps, "position">
>`
  border-radius: ${(props) => props.theme.borderRadius};
`;
export const BannerImageGridTwo = styled.div<
  Pick<BannerAnimationStyledProps, "position">
>`
  display: flex;
  gap: ${(props) => props.theme.spacing.m}px;
  z-index: 2;

  img {
    height: auto;
    object-fit: cover;
    border-radius: ${(props) => props.theme.borderRadius};
  }

  img:first-child {
    width: 40%;
  }

  img:nth-child(2) {
    width: 60%;
  }
`;

export const BannerImageGridThree = styled.div<
  Pick<BannerAnimationStyledProps, "position">
>`
  display: flex;
  flex-wrap: wrap;
  gap: ${(props) => props.theme.spacing.s};
  z-index: 2;

  img {
    display: block;
    height: auto;
    object-fit: cover;
    border-radius: ${(props) => props.theme.borderRadius};
  }

  img:first-child {
    width: 100%;
    height: 300px;
  }
  img:nth-child(2) {
    width: calc(33% - 8px);
    height: 130px;
  }
  img:nth-child(3) {
    width: calc(66% - 8px);
    height: 130px;
  }

  @media (min-width: ${mobileBreakPointCSS}) {
    gap: ${(props) => props.theme.spacing.xl};
    img:first-child {
      width: 45%;
      height: 200px;
    }
    img:nth-child(2) {
      position: absolute;
      width: 165px;
      height: 200px;
      left: 50%;
      transition: transform 2s ease-in-out;
      transform: translate(-50%, 100%);
    }
    &.is-inViewport {
      img:nth-child(2) {
        transform: translate(-50%, 50%);
      }
    }
    img:nth-child(3) {
      width: 38%;
      height: 200px;
    }
  }
`;
